<template>
  <Stack
    class="crm-linking-modal"
    gap="3"
  >
    <Stack gap="1">
      <Title>
        {{ title || 'Link Entity to CRM Contact' }}
      </Title>

      <Subtitle>
        {{ message || 'Please link this entity to a CRM contact.' }}
      </Subtitle>
    </Stack>

    <template
      v-if="crmOptions.length > 0"
    >
      <Stack gap="1">
        <Label>Select CRM</label>

        <Select
          id="crm-select"
          v-model="selectedCrm"
          :options="crmOptions"
        />
      </Stack>

      <CrmContactSearch
        :contacts="currentCrmContacts"
        :crm-type="selectedCrm"
        :is-fetching="isFetching"
        @contact-selected="handleContactSelected"
        @search="debouncedSearch"
      />
    </template>

    <Buttons v-else>
      <Button
        variant="primary"
        :to="{ name: 'oauth-integration-dashboard' }"
      >
        Connect a CRM
      </Button>
    </Buttons>

    <Buttons justify="between">
      <Button
        variant="primary"
        :disabled="selectedContactId === ''"
        @click="syncContact"
      >
        Generate
      </Button>
      <Button
        @click="close"
      >
        Cancel
      </Button>
    </Buttons>
  </Stack>
</template>

<script>
import CrmContactSearch from './CrmContactSearch.vue';
import { mapStores } from 'pinia';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore';
import { debounce } from 'lodash';
import { fetchWrapper } from '@frontend/shared/fetch-wrapper.js';
import { Button, Buttons, Label, Select, Stack, Subtitle, Title } from '@stakeui';

export default {
  name: 'CrmLinkingModal',
  components: {
    CrmContactSearch,
    Button,
    Buttons,
    Label,
    Select,
    Stack,
    Subtitle,
    Title,
  },
  props: {
    isActive: {
      type: Boolean,
      required: true,
    },
    meetingId: {
      type: String,
      default: null,
    },
    entityId: {
      type: String,
      default: null,
    },
    title: {
      type: String,
      default: '',
    },
    message: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isFetching: false,
      selectedCrm: null,
      selectedContactId: '',
      connectedCrms: [],
      wealthboxContacts: [],
      redtailContacts: [],
      salesforceContacts: [],
      searchTerm: '',
    };
  },
  computed: {
    ...mapStores(useAdvisorStore),
    headers() {
      return {
        'X-CSRFToken': this.$root.$data.csrfToken,
        'Content-Type': 'application/json',
      };
    },
    currentCrmContacts() {
      if (this.selectedCrm === 'Salesforce') {
        console.log('We have selected Salesforce');
        console.log(this.salesforceContacts);
        return this.salesforceContacts;
      }

      if (this.selectedCrm === 'Wealthbox') {
        return this.wealthboxContacts;
      }

      if (this.selectedCrm === 'Redtail') {
        return this.redtailContacts;
      }

      return [];
    },
    crmOptions() {
      return (this.connectedCrms || []).map(crm => ({
        label: crm,
        value: crm,
      }));
    },
  },
  watch: {
    selectedCrm() {
      this.fetchCrmContacts();
    },
  },
  mounted() {
    this.initialize();
    this.debouncedSearch = debounce(this.handleSearch, 300);
  },
  methods: {
    initialize() {
      this.connectedCrms = Object.keys(this.advisorStore.activeOAuthConnections)
          .filter(crm => ['Wealthbox', 'Redtail', 'Salesforce'].includes(crm));
      console.log('CONNECTED CRMS', this.connectedCrms);
      if (this.connectedCrms.length > 0) {
        this.selectedCrm = this.connectedCrms[0];
      }
    },
    async fetchCrmContacts() {
      if (!this.connectedCrms.includes(this.selectedCrm) || this.isFetching) return;

      this.isFetching = true;

      try {
        const searchParams = new URLSearchParams();
        if (this.searchTerm) {
          searchParams.append('search', this.searchTerm);
        }

        const response = await fetchWrapper(
            `/api/v1/crm/${this.selectedCrm.toLowerCase()}/contacts/?${searchParams.toString()}`,
            { headers: this.headers },
        );

        if (response.ok) {
          const data = await response.json();
          if (this.selectedCrm === 'Wealthbox') {
            this.wealthboxContacts = data.contacts;
          } else if (this.selectedCrm === 'Redtail') {
            this.redtailContacts = data.contacts;
          } else if (this.selectedCrm === 'Salesforce') {
            this.salesforceContacts = data.contacts;
          }
        }
      } catch (error) {
        console.error(`Error fetching ${this.selectedCrm} contacts:`, error);
      } finally {
        this.isFetching = false;
      }
    },
    async syncContact() {
      try {
        let url;
        let responseData;
        const payload = {
          crm_contact_id: this.selectedContactId,
          crm_type: this.selectedCrm,
        };

        if (this.meetingId) {
          // If we're linking a meeting
          url = `/api/v1/link-meeting-to-crm/${this.meetingId}/`;
        } else if (this.entityId) {
          // If we're linking an entity
          url = `/api/v1/entities/${this.entityId}/crm-contact/`;
        } else {
          throw new Error('Neither meetingId nor entityId provided');
        }

        const response = await fetch(url, {
          method: 'POST',
          headers: this.headers,
          body: JSON.stringify(payload),
        });

        if (response.ok) {
          responseData = await response.json();
          this.$emit('contact-linked', responseData);
          this.close();
        } else {
          alert('Failed to link CRM contact. Please try again.');
          console.error('Failed to link CRM contact:', await response.text());
        }
      } catch (error) {
        console.error('Error linking CRM contact:', error);
        alert('An error occurred while linking the CRM contact. Please try again.');
      }
    },
    async handleSearch(searchTerm) {
      if (['Redtail', 'Salesforce'].includes(this.selectedCrm)) {
        this.searchTerm = searchTerm;
        await this.fetchCrmContacts();
      }
    },
    handleContactSelected(contact) {
      if (!contact || !contact.id) {
        this.selectedContactId = '';
        return;
      }

      this.selectedContactId = contact.id;
    },
    close() {
      this.$emit('close');
    },
    getContactName(contact) {
      if (this.selectedCrm === 'Wealthbox') {
        return contact.first_name || contact.last_name ?
          `${contact.first_name} ${contact.last_name}`.trim() :
          contact.name || 'Unnamed Contact';
      } else if (this.selectedCrm === 'Redtail') {
        return contact.full_name ||
          `${contact.first_name} ${contact.last_name}`.trim() ||
          contact.name || 'Unnamed Contact';
      } else if (this.selectedCrm === 'Salesforce') {
        return contact.name || 'Unnamed Contact';
      }
      return 'Unknown Contact';
    },
  },
};
</script>

<style scoped>
.crm-linking-modal {
  min-width: 600px;
  padding: 1em;
}
</style>
